import React, {
  ReactElement, cloneElement, useEffect, useRef, useState,
} from "react"
import { Box, IconButton, Text } from "@chakra-ui/react"
import { useQueryClient } from "react-query"
import { FormType, Submission, SubmissionLineItem } from "sharedTypes"
import { prefetchEventFormSubmission, useEventFormSubmission } from "queries/formSubmissions"
import { refetchSubmissions } from "context/actions"
import { formIconAndType, submissionIconAndType } from "utilities/forms"
import { asDateWithTZ, asMoney } from "utilities/strings"
import Popover from "components/dialogs/Popover"
import MenuLink from "components/dialogs/Popover/MenuLink"
import {
  ActivityPopoverContent, ActivityPopoverRow, ActivityPopoverSection,
} from "components/dialogs/ActivityPopover"
import { FormSubmissionLineItemMenu } from "components/dialogs/FormSubmissionLineItemActivityPopup"
import Icon from "components/elements/Icon"
import Spinner from "components/elements/Spinner"

type Props = {
  trigger: ReactElement
  eventId: string
  submissionId: string
  currency: string
  timeZone: string
}

const priced = (type: FormType, price: string, currency: string) => (
  type === FormType.Ticket && Number(price) ? ` ${asMoney(price, currency)}` : ""
)

const payment = ({ paymentDetails, paymentMethod }: Submission) => paymentDetails ?? paymentMethod

const LineItemMenu = ({
  submission, lineItem, eventId, currency, timeZone, onDeleted,
}: {
  submission: Submission
  lineItem: SubmissionLineItem
  eventId: string
  currency: string
  timeZone: string
  onDeleted: () => void
}) => (
  <FormSubmissionLineItemMenu
    guestId={lineItem.guest.id}
    formType={lineItem.form.type}
    formSubmissionName={lineItem.name + priced(lineItem.form.type, lineItem.price, currency)}
    isTicket={lineItem.form.type === FormType.Ticket}
    externalId={lineItem.externalId}
    formSubmissionExternalId={submission.externalId}
    lineItemUrl={lineItem.url}
    receiptUrl={submission.url}
    eventId={eventId}
    payment={payment(submission)}
    createdAt={submission.createdAt}
    timeZone={timeZone}
    onDeleted={onDeleted}
  />
)

const SubmissionMenu = ({
  trigger, eventId, submissionId, currency, timeZone,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  // Stays loaded after closing, so a delete confirmation outlives the popover.
  const [wasOpened, setWasOpened] = useState(false)
  const [selectedId, setSelectedId] = useState<string>()
  const content = useRef<HTMLDivElement>(null)
  const backButton = useRef<HTMLButtonElement>(null)
  const returnTo = useRef<string>()
  const queryClient = useQueryClient()
  const { data: submission, isError } = useEventFormSubmission(eventId, wasOpened ? submissionId : "")

  const lineItems = submission?.lineItems ?? []
  const single = lineItems.length === 1 ? lineItems[0] : undefined
  const selected = single ?? lineItems.find(({ externalId }) => externalId === selectedId)
  const isTicket = lineItems.some(({ form }) => form.type === FormType.Ticket)

  useEffect(() => {
    if (selectedId) {
      backButton.current?.focus()
    } else {
      content.current?.querySelector<HTMLElement>(`[data-line-item="${returnTo.current}"]`)?.focus()
    }
  }, [selectedId])

  const open = () => {
    setSelectedId(undefined)
    setWasOpened(true)
    setIsOpen(true)
  }
  const prefetch = () => prefetchEventFormSubmission(queryClient, eventId, submissionId)
  const back = () => {
    returnTo.current = selectedId
    setSelectedId(undefined)
  }

  let header: ReactElement | string = ""
  if (selected) {
    header = (
      <Box as="span" display="block" position="relative" px={8}>
        {!single && (
          <IconButton
            ref={backButton}
            aria-label="Back"
            icon={<Icon icon="prev-arrow" size={3} />}
            variant="ghost"
            size="sm"
            position="absolute"
            left={-2}
            top="50%"
            transform="translateY(-50%)"
            onClick={back}
          />
        )}
        {selected.form.type + priced(selected.form.type, selected.price, currency)}
      </Box>
    )
  } else if (lineItems.length) {
    header = submissionIconAndType(lineItems).type
  }

  let body: ReactElement
  if (isError) {
    body = (
      <ActivityPopoverSection><Text>Submission couldn’t be loaded.</Text></ActivityPopoverSection>
    )
  } else if (!submission) {
    body = <Box h={24}><Spinner heightFull size="md" /></Box>
  } else if (selected) {
    body = (
      <LineItemMenu
        submission={submission}
        lineItem={selected}
        eventId={eventId}
        currency={currency}
        timeZone={timeZone}
        onDeleted={() => refetchSubmissions(queryClient, eventId)}
      />
    )
  } else {
    body = (
      <ActivityPopoverContent>
        <ActivityPopoverSection>
          <ActivityPopoverRow label="Submitted" value={asDateWithTZ(submission.createdAt, timeZone) || "N/A"} />
          {isTicket && <ActivityPopoverRow label="Payment" value={payment(submission)} />}
          <ActivityPopoverRow label="Reference" value={submission.externalId} />
        </ActivityPopoverSection>
        {isTicket && submission.url && (
          <Box as="a" href={submission.url} target="_blank">
            <MenuLink icon="orders-outline" label="Order Receipt" hasRightLinkIcon />
          </Box>
        )}
        {lineItems.map((lineItem) => (
          <Box
            key={lineItem.externalId}
            as="button"
            type="button"
            display="block"
            w="100%"
            textAlign="left"
            data-line-item={lineItem.externalId}
            onClick={() => setSelectedId(lineItem.externalId)}
          >
            <MenuLink
              icon={formIconAndType(lineItem.form.type).icon}
              label={lineItem.guest.name}
              description={lineItem.name + priced(lineItem.form.type, lineItem.price, currency)}
              hasChevron
            />
          </Box>
        ))}
      </ActivityPopoverContent>
    )
  }

  return (
    <Popover
      placement="bottom-start"
      isOpen={isOpen}
      onOpen={open}
      onClose={() => setIsOpen(false)}
      wrapTrigger={false}
      trigger={cloneElement(trigger, { onMouseEnter: prefetch, onFocus: prefetch })}
      header={header}
      body={<Box ref={content}>{body}</Box>}
    />
  )
}

export default SubmissionMenu
